// Roles.jsx - Main component with tabs and stats
import React, { useState, useEffect } from 'react';
import { Tabs, Tab, Spinner } from 'react-bootstrap';
import { Shield, RefreshCw, Users, CheckCircle } from 'lucide-react';
import { roleService } from '../../services';
import { useAuth } from '../../context';
import { PERMISSIONS } from '../../constants';
import RolesList from './RolesList';
import RolesManager from './RolesManager';
import './Roles.css';

const Roles = () => {
  const { hasPermission } = useAuth();

  const [activeTab, setActiveTab] = useState('view');
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    users: 0
  });
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  // Reference to child components for refresh
  const [listRefreshKey, setListRefreshKey] = useState(0);
  const [managerRefreshKey, setManagerRefreshKey] = useState(0);

  // ✅ Check if user can manage roles (any write permission)
  const canManageRoles =
    hasPermission(PERMISSIONS.ROLES.CREATE) ||
    hasPermission(PERMISSIONS.ROLES.EDIT) ||
    hasPermission(PERMISSIONS.ROLES.DELETE);

  // Load roles for stats
  useEffect(() => {
    loadRoles();
  }, []);

  // ✅ If user cannot manage, force view tab
  useEffect(() => {
    if (!canManageRoles && activeTab === 'manage') {
      setActiveTab('view');
    }
  }, [canManageRoles, activeTab]);

  const loadRoles = async () => {
    setLoading(true);
    const result = await roleService.getRoles();
    if (result.success) {
      const rolesData = result.data || [];
      setRoles(rolesData);
      setStats({
        total: rolesData.length,
        active: rolesData.filter(r => r.is_active !== false).length,
        users: rolesData.reduce((sum, r) => sum + (r.user_count || 0), 0)
      });
    }
    setLoading(false);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadRoles();
    setListRefreshKey(prev => prev + 1);
    setManagerRefreshKey(prev => prev + 1);
    setTimeout(() => {
      setRefreshing(false);
    }, 500);
  };

  return (
    <div className="roles-page">
      <div className="roles-container">
        {/* Header */}
        <div className="roles-header">
          <div className="roles-header-left">
            <div className="roles-header-icon">
              <Shield size={24} color="white" />
            </div>
            <div>
              <h1 className="roles-title">Roles</h1>
              <p className="roles-subtitle">Manage roles and permissions</p>
            </div>
          </div>
          <button 
            onClick={handleRefresh} 
            className="roles-refresh-button"
            disabled={refreshing}
          >
            <RefreshCw size={16} className={refreshing ? 'spinning' : ''} />
            Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="roles-stats-grid">
          {[
            { label: 'Total Roles', value: stats.total, icon: Shield, color: '#0ea5e9', bg: '#E0F2FE' },
            { label: 'Active Roles', value: stats.active, icon: CheckCircle, color: '#10B981', bg: '#D1FAE5' },
            { label: 'Total Users', value: stats.users, icon: Users, color: '#8B5CF6', bg: '#EDE9FE' }
          ].map((stat, index) => (
            <div key={index} className="roles-stat-card">
              <div>
                <div className="roles-stat-label">{stat.label}</div>
                <div className="roles-stat-value">
                  {loading ? (
                    <Spinner animation="border" size="sm" style={{ color: stat.color }} />
                  ) : (
                    stat.value
                  )}
                </div>
              </div>
              <div className="roles-stat-icon" style={{ background: stat.bg, color: stat.color }}>
                <stat.icon size={20} />
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="roles-tabs-wrapper">
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="roles-tabs"
            id="roles-tabs"
          >
            <Tab eventKey="view" title="View Roles">
              <div className="roles-tab-content">
                <RolesList key={listRefreshKey} />
              </div>
            </Tab>
            {/* ✅ Only show Manage tab if user has any write permission */}
            {canManageRoles && (
              <Tab eventKey="manage" title="Manage Roles">
                <div className="roles-tab-content">
                  <RolesManager key={managerRefreshKey} />
                </div>
              </Tab>
            )}
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Roles;